<!-- UID/PIN sign-in component that handles user authentication with UID and PIN
Features secure PIN input with visibility toggle and form validation
Implements direct authentication without OTP verification -->

<template>
  <!-- Authentication header section -->
  <div class="auth-header">
    <h1 class="auth-title">
      {{ LOGIN_CONSTANTS.SIGN_IN_TITLE }}
    </h1>
    <p class="auth-subtitle">
      {{ LOGIN_CONSTANTS.SIGN_IN_UID_PIN_SUBTITLE }}
    </p>
  </div>

  <!-- UID/PIN input form -->
  <v-form class="auth-form" @submit.prevent="onSubmit">
    <div class="form-group">
      <!-- UID input field -->
      <InputWithValidation
        id="uid"
        v-model="formData.uid"
        label="UID"
        :type="'text'"
        data-testid="uid-input"
        :placeholder="LOGIN_CONSTANTS.UID_PLACEHOLDER"
        aria-label="Enter your UID"
        name="uid"
      />

      <!-- PIN input field with visibility toggle -->
      <InputWithValidation
        id="pin_number"
        name="pin_number"
        :type="showPin ? 'text' : 'password'"
        label="PIN"
        data-testid="pin-input"
        :placeholder="LOGIN_CONSTANTS.PIN_PLACEHOLDER"
        aria-label="Enter your PIN"
        :click-append="togglePinVisibility"
        :append-icon="
          showPin ? ICON_CONSTANTS.EYE_OUTLINE : ICON_CONSTANTS.EYE_OFF_OUTLINE
        "
      />
    </div>

    <!-- Submit button -->
    <v-btn
      type="submit"
      class="submit-button"
      elevation="2"
      block
      :loading="isLoading"
      data-testid="uid-pin-submit-button"
    >
      {{ COMMON_CONSTANTS.BUTTON.SUBMIT }}
    </v-btn>
  </v-form>
</template>

<script setup lang="ts">
// Form validation and utility imports
import { toTypedSchema } from "@vee-validate/zod";
import { useForm } from "vee-validate";
import { ICON_CONSTANTS } from "~/constants/icon-constants";
import { COMMON_CONSTANTS } from "~/constants/common-constants";
import { LOGIN_CONSTANTS } from "~/constants/login-constants";
import {
  uidPinSchema,
  type UidPinModel,
} from "~/utils/validation/uidPinSchema";
import type { IUidPinLoginResponse, LoginRequestBody } from "~/types/login";
import { API_ENDPOINT } from "~/constants/api-endpoint";

// Form data initialization
const formData = ref<UidPinModel>({
  uid: "",
  pin_number: "",
});

const { showSnackbar } = useSnackbar();
// Form validation setup
const { handleSubmit, setFieldError } = useForm({
  validationSchema: toTypedSchema(uidPinSchema),
});

// Composables initialization
const { handleErrorResponse } = useErrors();
const router = useRouter();
const { isLoading } = useCommon();
const loginStore = useLoginStore();

// State management
const showPin = ref(false);
const errorMessage = ref("");
const loginType = useState<string>("loginType");
const organizationId = useState<string>("organizationId");

/**
 * Toggles PIN visibility between text and password
 */
const togglePinVisibility = () => {
  showPin.value = !showPin.value;
};

/**
 * Handles form submission
 * Validates UID/PIN and processes authentication
 * Redirects to home page on success
 */
const onSubmit = handleSubmit(async (values: UidPinModel) => {
  isLoading.value = true;

  // Prepare login request body
  const body: LoginRequestBody = {
    ...values,
    login_type: loginType.value,
    organization_id: organizationId.value,
    is_public_access: loginStore.loginTypeData.is_public_access,
  };

  try {
    // Send login request and handle response
    const response = await useApi().post<IUidPinLoginResponse>(
      API_ENDPOINT.LOGIN,
      body
    );

    // Update user store and redirect
    useUserStore().setUser(response.data);
    router.push("/home");
  } catch (error: any) {
    // Handle error response and show error message
    errorMessage.value = handleErrorResponse(error.response).error;
    showSnackbar(errorMessage.value, "error");

    // Handle field errors from server
    if (error.response.data.errors) {
      Object.entries(error.response.data.errors).forEach(([field, message]) => {
        setFieldError(field as keyof UidPinModel, message as string);
      });
    }
  } finally {
    // Reset isLoading state
    isLoading.value = false;
  }
});
</script>
